import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function Tim3DViewer() {
  const [isMobile, setIsMobile] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  // Detect if user is on mobile/tablet to show direct AR button vs QR code
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /android|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od|ad)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i;
      setIsMobile(mobileRegex.test(userAgent));
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    // Dynamically load Google's <model-viewer> script
    const scriptId = 'google-model-viewer-script';
    let script = document.getElementById(scriptId);

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'module';
      script.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/4.0.0/model-viewer.min.js';
      script.onload = () => setScriptLoaded(true);
      document.body.appendChild(script);
    } else {
      setScriptLoaded(true);
    }

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Get current page URL for QR code
  const currentUrl = window.location.href;
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(currentUrl)}`;

  return (
    <section id="ar-viewer" className="py-24 bg-[#2c3222] border-t border-b border-[#2c3222] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          
          {/* Left Column: Text & Instructions */}
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight leading-tight">
                Oplev TIM <br />i dit eget hjem
              </h2>
              <p className="text-lg text-slate-200 leading-relaxed">
                Med vores avancerede 3D- og AR-teknologi kan du placere TIM-robotten virtuelt i dine egne omgivelser. Se dens præcise størrelse, form og design direkte på dit bord eller gulv.
              </p>
            </div>

            {/* Interactive Instructions / Sizes Info */}
            <div className="space-y-6 bg-white/10 p-6 rounded-2xl border border-white/10">
              <h3 className="text-lg font-bold text-white flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shrink-0 shadow-md p-1.5">
                  <img
                    alt="TIM"
                    className="w-full h-full object-contain"
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=100,quality=90,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/y.png"
                  />
                </div>
                Forskellige størrelser til ethvert behov
              </h3>
              
              <p className="text-slate-200 text-sm leading-relaxed">
                TIM-dispenseren fås i tre forskellige størrelser, så den passer perfekt til den enkelte borgers medicineringsbehov:
              </p>

              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-3 bg-white/10 rounded-xl border border-white/10 shadow-xs">
                  <div className="font-extrabold text-white text-lg">MS8</div>
                  <div className="text-xs text-slate-300 mt-0.5">Op til 8 rum</div>
                </div>
                <div className="p-3 bg-white/10 rounded-xl border border-white/10 shadow-xs">
                  <div className="font-extrabold text-white text-lg">MS12</div>
                  <div className="text-xs text-slate-300 mt-0.5">Op til 12 rum</div>
                </div>
                <div className="p-3 bg-white/10 rounded-xl border border-white/10 shadow-xs">
                  <div className="font-extrabold text-white text-lg">MS16</div>
                  <div className="text-xs text-slate-300 mt-0.5">Op til 16 rum</div>
                </div>
              </div>

              <p className="text-slate-200 text-sm leading-relaxed">
                Hver model leverer den samme høje sikkerhed, automatiske påmindelser og direkte integration med kommunens omsorgssystemer.
              </p>

              <div className="pt-2">
                <Link 
                  to="/medimi-smart/tim" 
                  className="inline-flex items-center gap-2 text-sm font-bold text-white hover:text-white/80 transition-colors group"
                >
                  Læs mere om TIM-modellerne og specifikationer
                  <i className="fa-solid fa-arrow-right transition-transform group-hover:translate-x-1"></i>
                </Link>
              </div>
            </div>

            {/* Desktop QR Code removed from here */}
          </div>

          {/* Right Column: 3D Viewer Container */}
          <div className="lg:col-span-7">
            <div className="relative bg-white/5 rounded-3xl p-4 md:p-8 border border-white/10 shadow-xl overflow-hidden group">
              
              {/* Decorative background elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full filter blur-3xl -mr-20 -mt-20"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full filter blur-3xl -ml-20 -mb-20"></div>

              {/* 3D Model Viewer Canvas */}
              <div className="relative w-full h-[450px] md:h-[500px] bg-white rounded-2xl shadow-inner border border-slate-100 flex items-center justify-center overflow-hidden">
                
                {/* Floating QR Code in the corner */}
                {!isMobile && (
                  <div className="absolute top-4 right-4 z-20 bg-white/95 backdrop-blur-md p-3 rounded-2xl border border-slate-200/80 shadow-lg flex flex-col items-center text-center w-36 transition-all duration-300 hover:scale-105 gap-2">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-[#2c3222]/20 text-[#2c3222] rounded-full border border-[#2c3222]/20">
                      <span className="w-1 h-1 rounded-full bg-[#2c3222] animate-pulse"></span>
                      AR Oplevelse
                    </span>
                    <img 
                      src={qrCodeUrl} 
                      alt="Scan QR-kode for AR" 
                      className="w-24 h-24 rounded-lg"
                      loading="lazy"
                    />
                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold text-[#2c3222] uppercase tracking-wider">Prøv på mobil</span>
                      <span className="text-[9px] text-slate-500 leading-tight">Scan med kamera</span>
                    </div>
                  </div>
                )}
                
                {/* Google <model-viewer> element */}
                <model-viewer
                  src="/dummy-12.glb"
                  ar
                  ar-modes="webxr scene-viewer quick-look"
                  camera-controls
                  poster="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/y.png"
                  shadow-intensity="1.5"
                  shadow-softness="1"
                  auto-rotate
                  interaction-prompt="auto"
                  alt="3D model af TIM-robotten"
                  style={{ width: '100%', height: '100%', outline: 'none' }}
                  class="w-full h-full"
                >
                  {/* Custom AR Button inside model-viewer */}
                  <button 
                    slot="ar-button" 
                    className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-[#2c3222] hover:bg-[#2c3222]/80 text-white font-bold px-6 py-3 rounded-full shadow-lg transition-all duration-300 flex items-center gap-2.5 text-sm md:text-base border border-white/10"
                  >
                    <i className="fa-solid fa-cube text-lg"></i>
                    Se i dit rum (AR)
                  </button>

                  {/* Loading placeholder */}
                  <div slot="poster" className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50/80 backdrop-blur-sm transition-opacity duration-500">
                    <img 
                      src="https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=80,format=auto/assets/05bb43a3-4ef2-4771-9030-58242fc7345d/y.png" 
                      alt="TIM" 
                      className="w-40 h-40 object-contain animate-bounce"
                    />
                    <div className="flex items-center gap-2 text-slate-500 font-medium text-sm mt-4">
                      <i className="fa-solid fa-spinner animate-spin text-[#2c3222]"></i>
                      Indlæser 3D-model...
                    </div>
                  </div>
                </model-viewer>
              </div>

              {/* Interaction hints */}
              <div className="mt-4 flex flex-wrap items-center justify-center gap-6 text-xs text-slate-300 font-medium">
                <span className="flex items-center gap-1.5">
                  <i className="fa-solid fa-arrows-spin text-white"></i>
                  Træk for at rotere
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="fa-solid fa-magnifying-glass-plus text-white"></i>
                  Knib for at zoome
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="fa-solid fa-cube text-white"></i>
                  Understøtter iOS & Android AR
                </span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
